import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import Companies from './pages/Companies';
import CreateCompany from './pages/CreateCompany';
import EditCompany from './pages/EditCompany';
import CompanyDetails from './pages/CompanyDetails';
import InvoiceDetails from './pages/InvoiceDetails';
import CounterpartyDetails from './pages/CounterpartyDetails';
import TestAuth from './pages/TestAuth';
import HiddenChatPage from './pages/HiddenChatPage';
import { ChatPage } from './pages/ChatPage';
import CorrectionInvoices from './pages/CorrectionInvoices';
import ActivateUser from './pages/ActivateUser';
import UserMenu from './components/UserMenu';
import LanguageSelector from './components/LanguageSelector';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { useTranslation } from './hooks/useTranslation';
import { ToastProvider, useToast } from './components/Toast';
import { setToastFunction } from './services/apiClient';
import React, { useEffect } from 'react';

function Header() {
  const { user, isLoading } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const linkBase = 'px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors';
  const active = 'bg-gray-900 text-white hover:bg-gray-900';
  
  return (
    <header className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="/logo.png" 
            alt="FAKTURAMA" 
            className="h-9 w-9 rounded-lg object-contain"
          />
          <span className="text-xl font-semibold">FAKTURAMA</span>
        </div>
        <nav className="flex items-center gap-2">
          <NavLink to="/companies" className={({isActive}) => `${linkBase} ${isActive ? active : ''}`}>{t('nav.companies')}</NavLink>
        </nav>
        
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-500">{t('common.loading')}</span>
          </div>
        ) : user ? (
          <div className="flex items-center gap-3">
            <LanguageSelector 
              selectedLanguage={language}
              onLanguageChange={setLanguage}
              className="text-sm"
            />
            <UserMenu />
          </div>
        ) : (
          <LanguageSelector 
            selectedLanguage={language}
            onLanguageChange={setLanguage}
            className="text-sm"
          />
        )}
      </div>
    </header>
  );
}

function Page({title}: {title: string}) {
  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <h1 className="text-3xl font-bold mb-4">{title}</h1>
      <div className="rounded-2xl border border-gray-100 bg-white p-6 text-gray-600">Содержимое скоро будет…</div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AppInner() {
  const { isAuthenticated, isLoading } = useAuth();
  const { isLoading: languageLoading } = useLanguage();
  const { t } = useTranslation();
  
  if (isLoading || languageLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-lg">{t('common.loading')}</div>
      </div>
    );
  }
  
  return (
    <>
      <Header />
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />
        <Route path="/register" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Register />
        } />
        <Route path="/activate/:token" element={<ActivateUser />} />
        <Route path="/" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
        <Route path="/companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
        <Route path="/companies/new" element={<ProtectedRoute><CreateCompany /></ProtectedRoute>} />
        <Route path="/companies/:id" element={<ProtectedRoute><CompanyDetails /></ProtectedRoute>} />
        <Route path="/companies/:id/edit" element={<ProtectedRoute><EditCompany /></ProtectedRoute>} />
        <Route path="/companies/:companyId/counterparties/:counterpartyId" element={<ProtectedRoute><CounterpartyDetails /></ProtectedRoute>} />
        <Route path="/companies/:companyId/invoices/:invoiceId" element={<ProtectedRoute><InvoiceDetails /></ProtectedRoute>} />
        <Route path="/companies/:companyId/correction-invoices" element={<ProtectedRoute><CorrectionInvoices /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/hidden-chat" element={<ProtectedRoute><HiddenChatPage /></ProtectedRoute>} />
        <Route path="/test-auth" element={<TestAuth />} />
      </Routes>
    </>
  );
}

function AppWithToast() {
  const { showToast } = useToast();

  useEffect(() => {
    // Устанавливаем функцию показа уведомлений для apiClient
    setToastFunction(showToast);
  }, [showToast]);

  return <AppInner />;
}

function AppWithLanguage() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ToastProvider>
          <AppWithToast />
        </ToastProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppWithLanguage />
    </LanguageProvider>
  );
}

export default App;
